/**
 * Transport-free constants + the extractor call for the APKG (Anki) import.
 *
 * The upload itself is per-platform (XHR on web, `expo-file-system` on
 * mobile — both need progress), but WHAT is uploaded, how big it may be and
 * how the extractor is asked are the same on both clients.
 */
import type { ApkgStructuredExtraction } from './plan';

/** External extractor service (`apkg-exporter`). */
export const APKG_EXPORTER_ORIGIN = 'https://apkg-exporter.vercel.app';

/** Largest `.apkg` either client accepts before uploading. The presigned URL
 *  itself allows more (500MB) — this is the product limit shown to users. */
export const APKG_MAX_UPLOAD_BYTES = 150 * 1024 * 1024;

/** The limit as the `apkgUpload.maxSize` copy prints it. */
export const APKG_MAX_UPLOAD_LABEL = '150 MB';

/** `.apkg` has no IANA type; the presigned PUT is signed for this one, so the
 *  upload MUST send exactly it. */
export const APKG_UPLOAD_CONTENT_TYPE = 'application/octet-stream';

/** Anki exports are recognised by extension only (no MIME type to trust). */
export function isApkgFileName(name: string | null | undefined): boolean {
  return !!name && name.toLowerCase().endsWith('.apkg');
}

/** Response of `POST /api/apkg-get-presigned`. */
export type ApkgPresignResponse = { presignedUrl: string; filePath: string };

/**
 * The extractor answered with an error. `notAnApkg` is the one answer that is
 * about the FILE, not the service: the user picked something named `.apkg`
 * that is not a zip archive at all (FLASHKA-VITE-2W0). Both clients show the
 * same "Couldn't read the .apkg file" toast either way; only the rest — the
 * extractor failing on a real Anki export, e.g. "Failed to open Anki
 * collection database" (FLASHKA-VITE-1ZS) — is a bug worth reporting, and it
 * lives in the `apkg-exporter` service, not this repo.
 */
export class ApkgExtractionError extends Error {
  readonly status: number;
  readonly notAnApkg: boolean;
  /** The extractor's own answer, as it sent it. */
  readonly body: string;
  /**
   * Which request failed and on which file, so a report can be REPLAYED: the
   * bucket key fetches the exact `.apkg` the extractor choked on, and the
   * endpoints say whether the structured one already failed before the legacy
   * fallback answered.
   */
  readonly attempt: ApkgExtractionAttempt;

  constructor(
    status: number,
    statusText: string,
    body: string,
    attempt: ApkgExtractionAttempt = { endpoint: 'unknown', filePath: null },
  ) {
    super(`Extraction failed: ${statusText} - ${body}`);
    this.name = 'ApkgExtractionError';
    this.status = status;
    this.notAnApkg = NOT_A_ZIP.test(body);
    this.body = body;
    this.attempt = attempt;
  }
}

export type ApkgExtractionAttempt = {
  /** The endpoint whose answer is this error. */
  endpoint: string;
  /** The uploaded `.apkg`'s bucket key. */
  filePath: string | null;
  /** The structured endpoint's status, when it failed first and the legacy one answered. */
  structuredStatus?: number;
};

/** Python's `zipfile.BadZipFile` text, as the extractor relays it. */
const NOT_A_ZIP = /File is not a zip file/i;

/** The extractor refused the file itself — nothing on our side to fix. */
export const isUnreadableApkgError = (error: unknown): boolean =>
  error instanceof ApkgExtractionError && error.notAnApkg;

type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string },
) => Promise<{
  ok: boolean;
  status: number;
  statusText: string;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}>;

/**
 * Ask the extractor to turn an uploaded `.apkg` (its bucket `filePath`) into
 * cards + the Anki deck tree.
 *
 * Tries the structured endpoint first; on 404/405 (not deployed — or the blob
 * is genuinely missing, then the retry fails identically) or 5xx (the API doc
 * marks 500 as retryable-once) it retries on the legacy flat endpoint, whose
 * tree-less response degrades to the single-deck import in
 * `buildApkgImportPlan`.
 */
export async function extractApkg({
  filePath,
  userId,
  fetchImpl = fetch as unknown as FetchLike,
}: {
  filePath: string;
  userId: string | null | undefined;
  fetchImpl?: FetchLike;
}): Promise<ApkgStructuredExtraction> {
  const postExtract = (endpoint: string) =>
    fetchImpl(`${APKG_EXPORTER_ORIGIN}/api/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apkg_path: filePath, user_id: userId }),
    });

  let endpoint = 'extract-apkg-structured';
  let structuredStatus: number | undefined;
  let response = await postExtract(endpoint);
  if (response.status === 404 || response.status === 405 || response.status >= 500) {
    console.warn(
      `Structured APKG extraction unavailable (${response.status}), falling back to flat extraction`,
    );
    structuredStatus = response.status;
    endpoint = 'extract-apkg';
    response = await postExtract(endpoint);
  }

  if (!response.ok) {
    const errorData = await response.text();
    throw new ApkgExtractionError(response.status, response.statusText, errorData, {
      endpoint,
      filePath,
      ...(structuredStatus !== undefined ? { structuredStatus } : {}),
    });
  }

  return (await response.json()) as ApkgStructuredExtraction;
}
